import { yupResolver } from '@hookform/resolvers/yup';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { initializeHttpProvider } from '@/providers/HttpProvider/initializeHttpProvider';
import { RootState } from '@/redux/store';
import {
  IResWebhooksError,
  IResWebhooksOK,
  Webhooks,
} from '@/types/parcel/webhooks/webhooks.interface';

import { schema } from './schemaDevelopers';

interface IWebhook {
  data: Webhooks[];
  loading: boolean;
  error: IResWebhooksError | null | unknown;
}
const useDevelopers = () => {
  const [webhooks, setWebhooks] = useState<IWebhook>({
    data: [],
    loading: false,
    error: null,
  });
  const { profile } = useSelector((state: RootState) => state.parcel);
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm({
    resolver: yupResolver(schema),
    mode: 'onBlur',
  });

  const [copyCode, setCopyCode] = useState<HTMLElement | null>(null);

  const handleCopy = async (event: React.MouseEvent<HTMLButtonElement>, id: string) => {
    if (!id) return;
    setCopyCode(event.currentTarget);
    await navigator.clipboard.writeText(id);
    setTimeout(() => {
      setCopyCode(null);
    }, 1000);
  };

  const handleGetWebhooksData = useCallback(async () => {
    // Get
    const http = initializeHttpProvider();
    setWebhooks((prev) => ({ ...prev, loading: true }));
    try {
      const response = await http.webhooks.webhooks();
      const data = (response as IResWebhooksOK).data;
      setWebhooks({ data: data.webhooks, loading: false, error: null });
    } catch (err) {
      console.error('error', err);
      setWebhooks((prev) => ({ ...prev, loading: false, error: err }));
    }
  }, []);

  const handleDeleteWebhook = async (id: string) => {
    // Delete
    const http = initializeHttpProvider();
    try {
      await http.webhooks.deleteWebhook({ id });
      handleGetWebhooksData();
    } catch (err) {
      console.error('error', err);
    }
  };

  const handleCreateWebhook = async (url: string) => {
    // Post
    const http = initializeHttpProvider();
    try {
      await http.webhooks.createWebhook({ url });

      handleGetWebhooksData();

      reset({ url: '' });
    } catch (err) {
      console.error('error', err);
    }
  };

  useEffect(() => {
    handleGetWebhooksData();
  }, [handleGetWebhooksData]);

  return {
    credentials: profile.data?.credentials,
    register,
    watch,
    handleSubmit,
    errors,
    handleCopy,
    copyCode,
    webhooks,
    handleDeleteWebhook,
    handleCreateWebhook,
  };
};

export default useDevelopers;
